import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhotoUpload } from "@/components/PhotoUpload";
import { SPORTS } from "@/data/sports";
import { DAYS, todayISO } from "@/data/accountability";
import api, { formatApiError } from "@/lib/api";

export function PlanDialog({ open, onOpenChange, matchId, plan, onSaved }) {
  const [goal, setGoal] = useState(plan?.goal || "");
  const [activity, setActivity] = useState(plan?.activity || "gym");
  const [target, setTarget] = useState(plan?.target_per_week || 3);
  const [days, setDays] = useState(plan?.days || []);
  const [time, setTime] = useState(plan?.preferred_time || "");
  const [start, setStart] = useState(plan?.start_date || todayISO());
  const [end, setEnd] = useState(plan?.end_date || "");
  const [notes, setNotes] = useState(plan?.notes || "");
  const [saving, setSaving] = useState(false);

  const toggleDay = (d) => setDays((x) => (x.includes(d) ? x.filter((y) => y !== d) : [...x, d]));

  const submit = async () => {
    if (!goal.trim()) { toast.error("Please enter a goal."); return; }
    setSaving(true);
    try {
      const body = { goal, activity, target_per_week: Number(target), days, preferred_time: time, start_date: start, end_date: end || null, notes };
      if (plan) await api.put(`/plans/${plan.id}`, body);
      else await api.post("/plans", { match_id: matchId, ...body });
      toast.success(plan ? "Plan updated — awaiting confirmation." : "Plan proposed — awaiting confirmation.");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0B] border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight">{plan ? "Edit Plan" : "Create Accountability Plan"}</DialogTitle>
          <DialogDescription className="text-zinc-500">The other party will review and confirm before it becomes active.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label className="text-zinc-300">Goal</Label>
            <Input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="e.g. Improve fitness consistency" className="h-11 bg-zinc-900 border-zinc-800" data-testid="plan-goal-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label className="text-zinc-300">Activity</Label>
              <Select value={activity} onValueChange={setActivity}>
                <SelectTrigger className="h-11 bg-zinc-900 border-zinc-800" data-testid="plan-activity-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">{SPORTS.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-2"><Label className="text-zinc-300">Target / week</Label>
              <Input type="number" min={1} max={21} value={target} onChange={(e) => setTarget(e.target.value)} className="h-11 bg-zinc-900 border-zinc-800" data-testid="plan-target-input" /></div>
          </div>
          <div><Label className="text-zinc-300">Preferred days</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {DAYS.map((d) => (
                <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`plan-day-${d}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${days.includes(d) ? "bg-[#E53E3E] border-[#E53E3E] text-white" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>{d}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2"><Label className="text-zinc-300">Preferred time</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-11 bg-zinc-900 border-zinc-800" data-testid="plan-time-input" /></div>
            <div className="space-y-2"><Label className="text-zinc-300">Start date</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-11 bg-zinc-900 border-zinc-800" data-testid="plan-start-input" /></div>
            <div className="space-y-2"><Label className="text-zinc-300">End (optional)</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-11 bg-zinc-900 border-zinc-800" data-testid="plan-end-input" /></div>
          </div>
          <div className="space-y-2"><Label className="text-zinc-300">Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-zinc-900 border-zinc-800 min-h-[70px]" data-testid="plan-notes-input" /></div>
          <Button onClick={submit} disabled={saving} className="w-full h-11 bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold" data-testid="plan-submit-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (plan ? "Save & Re-propose" : "Propose Plan")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ScheduleDialog({ open, onOpenChange, matchId, defaultActivity, onSaved }) {
  const [activity, setActivity] = useState(defaultActivity || "gym");
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/activities", { match_id: matchId, activity, date, time });
      toast.success("Activity scheduled.");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0B] border-zinc-800 max-w-md" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black uppercase tracking-tight">Schedule Activity</DialogTitle>
          <DialogDescription className="text-zinc-500">Plan a session. Times show in each user's local timezone.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label className="text-zinc-300">Activity</Label>
            <Select value={activity} onValueChange={setActivity}>
              <SelectTrigger className="h-11 bg-zinc-900 border-zinc-800" data-testid="schedule-activity-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">{SPORTS.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label className="text-zinc-300">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 bg-zinc-900 border-zinc-800" data-testid="schedule-date-input" /></div>
            <div className="space-y-2"><Label className="text-zinc-300">Time (optional)</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-11 bg-zinc-900 border-zinc-800" data-testid="schedule-time-input" /></div>
          </div>
          <Button onClick={submit} disabled={saving} className="w-full h-11 bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold" data-testid="schedule-submit-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Schedule"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CheckinDialog({ open, onOpenChange, activity, onSaved }) {
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/checkins", { activity_id: activity.id, note, photo });
      toast.success("Check-in submitted for verification.");
      onSaved?.();
      onOpenChange(false);
      setNote(""); setPhoto("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0B] border-zinc-800 max-w-md" data-testid="checkin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black uppercase tracking-tight">Check In</DialogTitle>
          <DialogDescription className="text-zinc-500">Your evidence is private and only visible to your partner and support staff.</DialogDescription>
        </DialogHeader>
        {activity && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-400 capitalize">{activity.activity?.replace("-", " ")} · {activity.date}</p>
            <div className="space-y-2"><Label className="text-zinc-300">Note (optional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="How did it go?" className="bg-zinc-900 border-zinc-800 min-h-[70px]" data-testid="checkin-note-input" /></div>
            <div><Label className="text-zinc-300">Evidence photo (optional)</Label>
              <div className="mt-2"><PhotoUpload value={photo} onChange={setPhoto} testid="checkin-photo" /></div></div>
            <Button onClick={submit} disabled={saving} className="w-full h-11 bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold" data-testid="checkin-submit-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Check-in"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
